import { useEffect, useMemo, useState } from "react";
import { AlertCircle, BookMarked, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PX } from "@/constants/testIds";
import PxMasterForm from "@/components/px/PxMasterForm";
import {
  getPxCodeSystem,
  listPxCodeSystems,
  listPxMaster,
  setPxConceptActive,
} from "@/lib/pxMasterApi";
import "@/styles/emr-catalog.css";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((row) => row.msg).join("; ");
  return "Unable to load Px Master.";
}

export default function PxMasterPage() {
  const [items, setItems] = useState([]);
  const [system, setSystem] = useState("");
  const [mappingSystems, setMappingSystems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async (q) => {
    setLoading(true);
    setError(null);
    try {
      const [catalog, codeSystem, systems] = await Promise.all([
        listPxMaster({ q: q || undefined, active: null }),
        getPxCodeSystem(),
        listPxCodeSystems(),
      ]);
      setItems(catalog.items || []);
      setSystem(codeSystem.url || catalog.system || "");
      setMappingSystems((systems.items || []).filter((row) => row.id !== "px" && row.content === "not-present"));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => load(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="emr-catalog emr-catalog--full-table" data-testid={PX.page}>
      <div className="emr-catalog__pagehead">
        <div>
          <h1 className="emr-catalog__title" data-testid={PX.title}>
            Px Master
          </h1>
          <p className="emr-catalog__subtitle">
            Central procedure terminology for Surgical History, future Procedures, Orders, and
            Billing. Not a patient surgical history list.
          </p>
        </div>
        <Button
          type="button"
          data-testid={PX.newBtn}
          className="btn emr-catalog__btn"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <Plus className="emr-catalog__btn-icon" />
          New concept
        </Button>
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section
            data-testid={PX.banner}
            className="card emr-catalog__section emr-catalog__section--notice"
          >
            <h2 className="emr-catalog__section-title">Reference data</h2>
            <p className="emr-catalog__copy">
              Local FHIR CodeSystem: <span className="font-fhir">{system || "loading…"}</span>. External
              SNOMED CT / CPT / HCPCS / ICD-10-PCS mappings are optional and must use real codes.
            </p>
          </section>
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Search</h2>
            <div className="emr-catalog__filters emr-catalog__filters--search-only">
              <div className="emr-catalog__search">
                <Search className="emr-catalog__search-icon" aria-hidden="true" />
                <input
                  data-testid={PX.search}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search local Px Code or display"
                  className="emr-catalog__input"
                />
              </div>
            </div>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Px concepts</h2>
          {error ? (
            <div data-testid={PX.errorState} className="emr-catalog__error">
              <AlertCircle className="emr-catalog__error-icon" aria-hidden="true" />
              <p className="emr-catalog__error-copy">{error}</p>
              <Button variant="outline" className="btn emr-catalog__retry" onClick={() => load(query.trim())}>
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div data-testid={PX.loading} className="emr-catalog__status-msg">
              Loading Px Master…
            </div>
          ) : !filtered.length ? (
            <div data-testid={PX.emptyState} className="emr-catalog__empty">
              <BookMarked className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No Px concepts match this search.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={PX.table} className="striped highlight emr-catalog__table">
                <thead>
                  <tr>
                    <th>Px Code</th>
                    <th>Display</th>
                    <th>Contexts</th>
                    <th>Quick Entry</th>
                    <th>Active</th>
                    <th className="emr-catalog__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.id} data-testid={`${PX.row}-${row.localCode}`}>
                      <td className="font-fhir emr-catalog__name">{row.localCode}</td>
                      <td>{row.display}</td>
                      <td className="emr-catalog__muted">
                        {(row.contexts || []).join(", ") || "—"}
                      </td>
                      <td>
                        {row.quickEntry ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Yes</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">No</span>
                        )}
                      </td>
                      <td>
                        {row.active !== false ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Yes</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">No</span>
                        )}
                      </td>
                      <td>
                        <div className="emr-catalog__row-actions">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="emr-catalog-icon-btn"
                            data-testid={`${PX.editBtn}-${row.localCode}`}
                            onClick={() => {
                              setEditing(row);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <button
                            type="button"
                            className="emr-catalog__text-btn"
                            data-testid={`${PX.activateBtn}-${row.localCode}`}
                            onClick={async () => {
                              try {
                                await setPxConceptActive(row.id, row.active === false);
                                toast.success(row.active === false ? "Activated." : "Deactivated.");
                                load(query.trim());
                              } catch (err) {
                                toast.error(apiErrorMessage(err));
                              }
                            }}
                          >
                            {row.active === false ? "Activate" : "Deactivate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <PxMasterForm
        open={formOpen}
        editing={editing}
        mappingSystems={mappingSystems}
        onClose={() => setFormOpen(false)}
        onSaved={() => load(query.trim())}
      />
    </div>
  );
}
